if (!customElements.get('upsell-target')) {
  class UpsellTarget extends HTMLElement {
    constructor() {
      super();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.initialized = false;
      window.__upsellFetchCache = {};
    }

    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const wrapper = this.closest('[data-set-upsell-wrapper]');
      const atcWrapper = (wrapper || this).closest('coco-shuffle')?.querySelector('[data-observe-atc].flex');
      if (atcWrapper && atcWrapper.parentNode && wrapper && !wrapper.dataset.moved) {
        this.moving = true;
        wrapper.dataset.moved = '';
        atcWrapper.parentNode.insertBefore(wrapper, atcWrapper);
        this.moving = false;
      }

      const isVisible = this.style.display !== 'none';
      if (isVisible) {
        this.renderSkeleton();
        await new Promise(resolve => requestAnimationFrame(resolve));
      }

      let match = null;

      if (this.dataset.upsellHandle) {
        match = await this.fetchProduct(this.dataset.upsellHandle);
      }

      if (!match) {
        const allProducts = await this.fetchAllProducts(this.dataset.collection);
        match = allProducts.find(({ tags }) =>
          tags.includes(this.dataset.category)
          && (!this.dataset.gender || tags.includes(this.dataset.gender))
          && (!this.dataset.color || tags.includes(this.dataset.color))
        );
      }

      if (!match) {
        this.innerHTML = '';
        return;
      }

      window.__upsellProduct = match;
      this.product = match;
      this.availableVariants = match.variants.filter(v => v.available !== false);
      this.selectedVariant = null;
      this.defaultVariant = this.availableVariants[0] || match.variants[0];

      this.render();
      this.addListeners();
    }

    renderSkeleton() {
      this.innerHTML = `<div class="upsell-shimmer w-full h-28 rounded-xl mb-4"></div>`;
    }

    render() {
      const { product, defaultVariant } = this;
      const heading = this.getHeading();
      const defaultPrice = this.formatPrice(defaultVariant?.price);

      const availableVariants = this.availableVariants;
      if (!availableVariants.length) {
        this.innerHTML = '';
        return;
      }

      const optionsHtml = product.options.map((option, i) => {
        const key = `option${i + 1}`;
        const values = [...new Set(availableVariants.map(v => v[key]).filter(Boolean))];
        return `
          <span class="upsell-select-wrap">
            <select data-option-index="${i}">
              <option value="">${option.name}</option>
              ${values.map(val => `<option value="${val}">${val}</option>`).join('')}
            </select>
            <svg class="pointer-events-none w-3 h-3 mr-2.5 flex-none" viewBox="0 0 12 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 1.5l5 5 5-5"/>
            </svg>
          </span>
        `;
      }).join('');

      this.innerHTML = `
        <div class="flex items-center gap-3 border border-[#BBB] rounded-xl p-3 mb-4">
          <label data-checkbox-wrap class="upsell-checkbox flex-none w-6 h-6 border border-[#373737] rounded cursor-pointer flex items-center justify-center transition-colors">
            <input type="checkbox" data-checkbox class="!hidden" />
            <svg data-checkmark class="hidden" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M5 9.23529L7.26471 11.5L12.7647 6" stroke="#2C2E34" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </label>
          <div class="flex-1 min-w-0 flex items-stretch gap-3">
            <a href="/products/${product.handle}" class="flex-none">
              <img
                class="h-full max-h-[66px] aspect-square object-cover rounded-lg"
                src="${product.images[0]?.src || ''}"
                alt="${product.title}"
              />
            </a>
            <div class="flex-1 min-w-0 flex flex-col gap-2">
              <div class="flex items-start justify-between gap-2">
                <div class="flex flex-col gap-2">
                  <div class="font-semibold text-sm leading-none">${heading}</div>
                  <div class="text-xs text-secondaryText leading-none">${product.title}</div>
                </div>
                <span data-price class="hidden md:inline text-xs font-medium whitespace-nowrap ml-auto">+ ${defaultPrice}</span>
              </div>
              <div data-options-wrap class="flex items-center gap-2 w-fit">
                <div data-options class="invisible contents">
                  ${optionsHtml}
                </div>
                <span data-price-mobile class="text-xs font-medium whitespace-nowrap ml-auto md:hidden order-first">+ ${defaultPrice}</span>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    addListeners() {
      const checkbox = this.querySelector('[data-checkbox]');
      const checkboxWrap = this.querySelector('[data-checkbox-wrap]');
      const checkmark = this.querySelector('[data-checkmark]');
      const optionsArea = this.querySelector('[data-options]');

      const priceMobile = this.querySelector('[data-price-mobile]');

      checkbox.addEventListener('change', () => {
        const { checked } = checkbox;
        optionsArea.classList.toggle('invisible', !checked);
        optionsArea.classList.toggle('visible', checked);
        checkboxWrap.classList.toggle('bg-primaryBrand', checked);
        checkmark.classList.toggle('hidden', !checked);
        if (priceMobile) {
          priceMobile.classList.toggle('order-first', !checked);
          priceMobile.classList.toggle('order-last', checked);
        }
      });

      this.querySelectorAll('.upsell-select-wrap').forEach(wrap => {
        const select = wrap.querySelector('select');
        wrap.addEventListener('click', (e) => {
          if (e.target !== select) select.showPicker?.();
        });
      });

      this.querySelectorAll('select[data-option-index]').forEach(select => {
        select.addEventListener('change', () => this.updateVariant());
      });

      const optionsWrap = this.querySelector('[data-options-wrap]');

      window.addEventListener('cart:collect-items', (e) => {
        if (this.offsetParent === null) return;
        if (!checkbox.checked) return;
        if (!this.selectedVariant) {
          optionsWrap.classList.add('ring-2', 'ring-red-400', 'rounded-lg', 'p-1');
          e.detail.invalid = true;
          return;
        }
        optionsWrap.classList.remove('ring-2', 'ring-red-400', 'rounded-lg', 'p-1');
        e.detail.items.push({ id: this.selectedVariant.id, qty: 1 });
      });
    }

    updateVariant() {
      const selects = [...this.querySelectorAll('select[data-option-index]')];
      const selectedOptions = selects.map(s => s.value);

      selects.forEach((select, i) => {
        const key = `option${i + 1}`;
        const compatible = this.availableVariants.filter(v =>
          selectedOptions.every((opt, j) => j === i || !opt || v[`option${j + 1}`] === opt)
        );
        const values = [...new Set(compatible.map(v => v[key]).filter(Boolean))];
        const current = select.value;
        select.querySelectorAll('option[value]:not([value=""])').forEach(opt => {
          const isAvailable = values.includes(opt.value);
          opt.disabled = !isAvailable;
          opt.hidden = !isAvailable;
        });
        if (current && !values.includes(current)) {
          select.value = '';
          selectedOptions[i] = '';
        }
      });

      if (selectedOptions.some(o => !o)) {
        this.selectedVariant = null;
        return;
      }

      this.selectedVariant = this.availableVariants.find(v =>
        selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt)
      ) || null;

      const priceText = this.selectedVariant
        ? `+ ${this.formatPrice(this.selectedVariant.price)}`
        : `+ ${this.formatPrice(this.defaultVariant?.price)}`;
      this.querySelectorAll('[data-price], [data-price-mobile]').forEach(el => {
        el.textContent = priceText;
      });

      if (this.selectedVariant) {
        this.querySelector('[data-options-wrap]')?.classList.remove('ring-2', 'ring-red-400', 'rounded-lg', 'p-1');
      }
    }

    formatPrice(price) {
      if (!price) return '';
      const num = parseFloat(price);
      return num % 1 === 0 ? `$${num}` : `$${num.toFixed(2)}`;
    }

    getHeading() {
      const category = this.dataset.category || '';
      if (category.includes('Scrub Pant')) return 'Add the Matching Bottom';
      if (category.includes('Scrub Top')) return 'Add the Matching Top';

      const title = (this.product?.title || '').toLowerCase();
      if (title.includes('pant') || title.includes('bottom') || title.includes('jogger')) return 'Add the Matching Bottom';
      if (title.includes('top') || title.includes('scrub')) return 'Add the Matching Top';

      return 'Add the Matching Set';
    }

    async fetchProduct(handle) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        return {
          ...product,
          images: (product.images || []).map(src => ({ src })),
          variants: product.variants.map(v => ({
            ...v,
            price: (v.price / 100).toFixed(2),
            compare_at_price: v.compare_at_price != null ? (v.compare_at_price / 100).toFixed(2) : v.compare_at_price,
          })),
        };
      } catch {
        return null;
      }
    }

    fetchAllProducts(collection) {
      window.__upsellFetchCache = window.__upsellFetchCache || {};
      if (!window.__upsellFetchCache[collection]) {
        window.__upsellFetchCache[collection] = this.doFetch(collection);
      }
      return window.__upsellFetchCache[collection];
    }

    async doFetch(collection) {
      let page = 1;
      let allProducts = [];

      while (true) {
        const res = await fetch(`/collections/${collection}/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const { products } = await res.json();
        if (!products.length) break;
        allProducts = allProducts.concat(products);
        if (products.length < 250) break;
        page++;
      }

      return allProducts;
    }
  }

  customElements.define('upsell-target', UpsellTarget);
}
